import { useMemo } from "react";
import { Timestamp } from "firebase/firestore";
import type { ReportDocument, ReportEntryDocument } from "./types";
import { chunkRowsByConfig, type FixedRowPaginationConfig } from "./fixedRowPagination";

type ReportCellValue = string | Timestamp | null | undefined;

type RealizedProfitLossRow = {
  currency: string;
  date: string;
  time: string;
  transactionType: string;
  refNo: string;
  securityCode: string;
  securityName: string;
  quantity: string;
  price: string;
  avgCost: string;
  commission: string;
  profitAmount: string;
  profitAmountValue: number;
};

type RealizedProfitLossCurrencyGroup = {
  currency: string;
  rows: RealizedProfitLossRow[];
  totalProfitAmount: string;
};

const REALIZED_GL_PAGINATION: FixedRowPaginationConfig = {
  firstPageRows: 28,
  continuationRows: 30,
};

const currencyCodeMap: Record<string, string> = {
  "Kuwaiti Dinar": "KWD",
  "US Dollar": "USD",
  "Saudi Riyal": "SAR",
};

const parseNumericValue = (value: ReportCellValue): number | null => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return null;
  }

  const parsed = Number(value.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const isTimestampValue = (value: unknown): value is Timestamp => value instanceof Timestamp;

const formatDateValue = (value: ReportCellValue): string => {
  if (!value) {
    return "";
  }

  if (isTimestampValue(value)) {
    const date = value.toDate();
    const day = `${date.getDate()}`.padStart(2, "0");
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const match = value.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
  }

  return value;
};

const formatQuantityValue = (value: ReportCellValue): string => {
  const parsed = parseNumericValue(value);
  if (parsed === null) {
    return "";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(parsed);
};

const formatDerivedAvgCostValue = (costValue: ReportCellValue, qtyValue: ReportCellValue): string => {
  const cost = parseNumericValue(costValue);
  const qty = parseNumericValue(qtyValue);
  if (cost === null || qty === null || Math.abs(qty) < 0.0000001) {
    return "";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  }).format(cost / qty);
};

const formatTransactionType = (value: ReportCellValue): string => {
  if (typeof value !== "string" || value.trim().length === 0) {
    return "";
  }

  if (value === "AccountingRelated") {
    return "Journal Voucher";
  }

  if (value === "Buy" || value === "Sell") {
    return `Security ${value}`;
  }

  return value;
};

const formatCurrencyLabel = (currencyValue?: string): string => {
  if (!currencyValue) {
    return "";
  }

  return currencyCodeMap[currencyValue] ?? currencyValue;
};

const formatBracketedMoneyValue = (value: ReportCellValue): string => {
  const parsed = parseNumericValue(value);
  if (parsed === null) {
    return "";
  }

  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(Math.abs(parsed));

  return parsed < 0 ? `(${formatted})` : formatted;
};

const formatSixDecimalValue = (value: ReportCellValue): string => {
  const parsed = parseNumericValue(value);
  if (parsed === null) {
    return "";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  }).format(parsed);
};

const formatTimeValue = (value: ReportCellValue): string => {
  if (!value) {
    return "";
  }

  const formatTimeParts = (hours: number, minutes: number, seconds: number) => {
    return `${`${hours}`.padStart(2, "0")}:${`${minutes}`.padStart(2, "0")}.${`${seconds}`.padStart(2, "0")}`;
  };

  if (isTimestampValue(value)) {
    const date = value.toDate();
    return formatTimeParts(date.getHours(), date.getMinutes(), date.getSeconds());
  }

  const timeMatch = value.match(/(\d{2}):(\d{2}):(\d{2})/);
  if (timeMatch) {
    const [, hours, minutes, seconds] = timeMatch;
    return formatTimeParts(Number(hours), Number(minutes), Number(seconds));
  }

  const dottedMatch = value.match(/(\d{2}):(\d{2})\.(\d{2})/);
  if (dottedMatch) {
    return value;
  }

  return value;
};

const formatGeneratedAtValue = (date: Date): string => {
  const day = `${date.getDate()}`.padStart(2, "0");
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const year = date.getFullYear();
  const hours24 = date.getHours();
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  const seconds = `${date.getSeconds()}`.padStart(2, "0");
  const period = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${day}/${month}/${year} ${hours12}:${minutes}:${seconds}${period}`;
};

const buildColumnIndex = (columns: string[]): Record<string, number> => {
  return columns.reduce<Record<string, number>>((indexMap, column, index) => {
    indexMap[column] = index;
    return indexMap;
  }, {});
};

const getCellValue = (
  entry: Array<string | Timestamp | null>,
  columnIndex: Record<string, number>,
  columnName: string,
): ReportCellValue => {
  const index = columnIndex[columnName];
  if (index === undefined) {
    return undefined;
  }

  return entry[index];
};

const getCellValueByCandidates = (
  entry: Array<string | Timestamp | null>,
  columnIndex: Record<string, number>,
  columnNames: string[],
): ReportCellValue => {
  for (const columnName of columnNames) {
    const value = getCellValue(entry, columnIndex, columnName);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return undefined;
};

const parseGenevaParameterDate = (value?: string): string => {
  if (!value) {
    return "";
  }

  const match = value.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
  if (!match) {
    return value;
  }

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

type RealizedProfitLossTemplateProps = {
  report: ReportDocument;
  entries: ReportEntryDocument[];
};

export default function RealizedProfitLossTemplate({
  report,
  entries,
}: RealizedProfitLossTemplateProps) {
  const columns = report.result?.columns || [];
  const columnIndex = useMemo(() => buildColumnIndex(columns), [columns]);
  const generatedAt = useMemo(() => formatGeneratedAtValue(new Date()), []);

  const rows = useMemo<RealizedProfitLossRow[]>(() => {
    return entries.map((entryDoc) => {
      const entry = entryDoc.entry || [];
      const acctDate = getCellValueByCandidates(entry, columnIndex, ["AcctDate", "ClosingDate", "ADate", "SDate", "TDate", "DateTime", "EventDate"]);
      const qtyValue = getCellValueByCandidates(entry, columnIndex, ["Qty", "Quantity"]);
      const closingPriceValue = getCellValueByCandidates(entry, columnIndex, ["ClosingPrice", "Price", "TradePrice"]);
      const costLocalValue = getCellValueByCandidates(entry, columnIndex, ["CostLocal", "CostBook"]);
      const netProceedsLocalValue = getCellValueByCandidates(entry, columnIndex, ["NetProceedsLocal", "NetProceedsBook"]);
      const qtyNumeric = parseNumericValue(qtyValue) ?? 0;
      const closingPriceNumeric = parseNumericValue(closingPriceValue) ?? 0;
      const netProceedsNumeric = parseNumericValue(netProceedsLocalValue) ?? 0;
      const derivedCommission = Math.abs((qtyNumeric * closingPriceNumeric) - netProceedsNumeric);
      const profitAmountValue =
        parseNumericValue(getCellValueByCandidates(entry, columnIndex, ["TotalGLBook", "TotalGLLocal", "PriceGainLocal", "PriceGainBook", "ProfitAmount", "ProfitLoss", "RealizedGainLoss", "RealizedGL", "GainLoss"]))
        ?? 0;

      return {
        currency: `${getCellValueByCandidates(entry, columnIndex, ["Ccy", "Currency", "BookCurrency"]) ?? ""}`,
        date: formatDateValue(
          acctDate,
        ),
        time: formatTimeValue(
          getCellValueByCandidates(entry, columnIndex, ["Time", "ATime", "STime", "TTime"]) ?? acctDate,
        ),
        transactionType: formatTransactionType(
          getCellValueByCandidates(entry, columnIndex, ["TranType", "EventDesc", "TransDesc", "TransactionType"]),
        ),
        refNo: `${getCellValueByCandidates(entry, columnIndex, ["TransID", "RefNo", "ReferenceNo"]) ?? ""}`,
        securityCode: `${getCellValueByCandidates(entry, columnIndex, ["InvestCode", "UnderlyingInvestment", "SecurityCode", "InvestmentCode"]) ?? ""}`,
        securityName: `${getCellValueByCandidates(entry, columnIndex, ["IDesc", "Invest", "SecurityName", "InvestmentName", "UnderlyingInvestmentName"]) ?? ""}`,
        quantity: formatQuantityValue(qtyValue),
        price: formatSixDecimalValue(closingPriceValue),
        avgCost: formatDerivedAvgCostValue(costLocalValue, qtyValue),
        commission: derivedCommission > 0.0000001 ? formatBracketedMoneyValue(String(derivedCommission)) : "",
        profitAmount: formatBracketedMoneyValue(getCellValueByCandidates(entry, columnIndex, ["TotalGLBook", "TotalGLLocal", "PriceGainLocal", "PriceGainBook", "ProfitAmount", "ProfitLoss", "RealizedGainLoss", "RealizedGL", "GainLoss"])),
        profitAmountValue,
      };
    }).filter((row) => {
      return Boolean(
        row.refNo
        || row.transactionType
        || row.securityCode
        || row.securityName
        || row.quantity
        || row.price
        || row.avgCost
        || row.commission
        || row.profitAmount,
      );
    });
  }, [columnIndex, entries]);

  const portfolioLabel = [
    report.parameters?.Portfolio || report.portfolioId || "",
    report.parameters?.PortfolioDescription || "",
  ].filter(Boolean).join(" ");
  const fromDate = parseGenevaParameterDate(report.parameters?.PeriodStartDate);
  const toDate = parseGenevaParameterDate(report.parameters?.PeriodEndDate);

  const currencyGroups = useMemo<RealizedProfitLossCurrencyGroup[]>(() => {
    const groupedRows = new Map<string, RealizedProfitLossRow[]>();
    const order: string[] = [];

    rows.forEach((row) => {
      const currency = row.currency || "Base";
      if (!groupedRows.has(currency)) {
        groupedRows.set(currency, []);
        order.push(currency);
      }

      groupedRows.get(currency)?.push(row);
    });

    return order.map((currency) => {
      const currencyRows = groupedRows.get(currency) || [];
      const totalProfitAmount = formatBracketedMoneyValue(
        String(currencyRows.reduce((sum, row) => sum + row.profitAmountValue, 0)),
      );

      return {
        currency,
        rows: currencyRows,
        totalProfitAmount,
      };
    }).filter((group) => group.rows.length > 0);
  }, [rows]);

  const pages = useMemo(() => {
    return currencyGroups.flatMap((group) => {
      const groupPages = chunkRowsByConfig(group.rows, REALIZED_GL_PAGINATION);
      return groupPages.map((page) => ({
        ...group,
        rows: page.rows,
        isLastPage: page.isLastPage,
      }));
    });
  }, [currencyGroups]);

  return (
    <div className="report-print-stack">
      {pages.map((page, pageIndex) => (
        <section className="statement-page" key={`realized-page-${pageIndex + 1}-${page.currency}`}>
          <header className="statement-header">
            <div className="statement-header-meta">
              <div>{generatedAt}</div>
              <div>Page {pageIndex + 1} of {pages.length}</div>
            </div>

            <div className="statement-header-center">
              <div className="statement-title">Realized Profit / (Loss)</div>
              <div>
                <strong>From Date</strong> : {fromDate}
                <span style={{ display: "inline-block", width: "1rem" }} />
                <strong>To Date</strong> : {toDate}
              </div>
            </div>

            <div className="statement-header-right">
              <div className="statement-currency">
                Currency : {formatCurrencyLabel(page.currency)}
              </div>
            </div>
          </header>

          <div className="statement-realized-portfolio">
            <strong>Portfolio</strong> : {portfolioLabel}
          </div>

          <table className="statement-table statement-realized-table">
            <colgroup>
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "27%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Transaction Type</th>
                <th>Ref. No.</th>
                <th>Security</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Avg. Cost</th>
                <th>Commission</th>
                <th>Profit Amount</th>
              </tr>
            </thead>
            <tbody>
              {page.rows.map((row, rowIndex) => (
                <tr key={`realized-row-${pageIndex}-${rowIndex}-${row.refNo}`}>
                  <td className="align-left">{row.date}</td>
                  <td className="align-left">{row.time}</td>
                  <td className="align-left">{row.transactionType}</td>
                  <td className="align-left">{row.refNo}</td>
                  <td className="align-left">
                    {[row.securityCode, row.securityName].filter(Boolean).join("   ")}
                  </td>
                  <td className="align-right">{row.quantity}</td>
                  <td className="align-right">{row.price}</td>
                  <td className="align-right">{row.avgCost}</td>
                  <td className="align-right">{row.commission}</td>
                  <td className="align-right">{row.profitAmount}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {page.isLastPage && (
            <div className="statement-summary">
              <table className="statement-summary-table">
                <tbody>
                  <tr>
                    <td className="statement-summary-label">Total :</td>
                    <td className="statement-summary-aux" />
                    <td className="statement-summary-value">{page.totalProfitAmount}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
